/**
 * Rótulos das relações pessoais — espelham o enum `TipoRelacaoPessoal` do backend.
 * Agrupados por natureza só para o select ficar navegável. A semântica da aresta é
 * "a pessoa relacionada é <tipo> de você": escolher "Pai" registra que a pessoa
 * buscada é o pai desta. O backend cria a recíproca sozinho: filho/filha quando ele
 * sabe o sexo desta pessoa, e filho_a quando não sabe — nunca mais o masculino por
 * omissão, que é o que gravava toda filha como «filho».
 */
export type GrupoRelacao = { grupo: string; itens: { valor: string; rotulo: string }[] };

export const RELACOES: GrupoRelacao[] = [
  {
    grupo: 'Parentesco direto',
    itens: [
      { valor: 'pai', rotulo: 'Pai' },
      { valor: 'mae', rotulo: 'Mãe' },
      { valor: 'filho', rotulo: 'Filho' },
      { valor: 'filha', rotulo: 'Filha' },
      { valor: 'irmao', rotulo: 'Irmão' },
      { valor: 'irma', rotulo: 'Irmã' },
    ],
  },
  {
    grupo: 'Parentesco estendido',
    itens: [
      { valor: 'avo', rotulo: 'Avô' },
      { valor: 'ava', rotulo: 'Avó' },
      { valor: 'neto', rotulo: 'Neto' },
      { valor: 'neta', rotulo: 'Neta' },
      { valor: 'tio', rotulo: 'Tio' },
      { valor: 'tia', rotulo: 'Tia' },
      { valor: 'sobrinho', rotulo: 'Sobrinho' },
      { valor: 'sobrinha', rotulo: 'Sobrinha' },
      { valor: 'primo', rotulo: 'Primo' },
      { valor: 'prima', rotulo: 'Prima' },
    ],
  },
  {
    grupo: 'Afinidade',
    itens: [
      { valor: 'conjuge', rotulo: 'Cônjuge' },
      { valor: 'ex_conjuge', rotulo: 'Ex-cônjuge' },
      { valor: 'companheiro', rotulo: 'Companheiro(a)' },
      { valor: 'padrasto', rotulo: 'Padrasto' },
      { valor: 'madrasta', rotulo: 'Madrasta' },
      { valor: 'enteado', rotulo: 'Enteado' },
      { valor: 'enteada', rotulo: 'Enteada' },
      { valor: 'cunhado', rotulo: 'Cunhado' },
      { valor: 'cunhada', rotulo: 'Cunhada' },
      { valor: 'sogro', rotulo: 'Sogro' },
      { valor: 'sogra', rotulo: 'Sogra' },
      { valor: 'genro', rotulo: 'Genro' },
      { valor: 'nora', rotulo: 'Nora' },
    ],
  },
  {
    grupo: 'Social',
    itens: [
      { valor: 'amigo', rotulo: 'Amigo(a)' },
      { valor: 'colega', rotulo: 'Colega' },
      { valor: 'socio', rotulo: 'Sócio(a)' },
      { valor: 'vizinho', rotulo: 'Vizinho(a)' },
      { valor: 'outro', rotulo: 'Outro' },
    ],
  },
  {
    // Para quando o grau se sabe e o gênero não. Escolher um gendrado acima AFIRMA o sexo da
    // pessoa e o grava na ficha dela; escolher daqui não afirma nada, que é o ponto.
    grupo: 'Sem gênero definido',
    itens: [
      { valor: 'genitor', rotulo: 'Genitor(a)' },
      { valor: 'filho_a', rotulo: 'Filho(a)' },
      { valor: 'irmao_a', rotulo: 'Irmão(ã)' },
      { valor: 'avo_o', rotulo: 'Avô(ó)' },
      { valor: 'neto_a', rotulo: 'Neto(a)' },
      { valor: 'tio_a', rotulo: 'Tio(a)' },
      { valor: 'sobrinho_a', rotulo: 'Sobrinho(a)' },
      { valor: 'primo_a', rotulo: 'Primo(a)' },
      { valor: 'padrasto_madrasta', rotulo: 'Padrasto/Madrasta' },
      { valor: 'enteado_a', rotulo: 'Enteado(a)' },
      { valor: 'cunhado_a', rotulo: 'Cunhado(a)' },
      { valor: 'sogro_a', rotulo: 'Sogro(a)' },
      { valor: 'genro_nora', rotulo: 'Genro/Nora' },
      { valor: 'parente', rotulo: 'Parente (grau não determinado)' },
    ],
  },
];

const MAPA = new Map(RELACOES.flatMap((g) => g.itens.map((i) => [i.valor, i.rotulo])));

export const rotuloRelacao = (valor: string): string => MAPA.get(valor) ?? valor;
